// Utility for PDF export using html2pdf.js
import html2pdf from "html2pdf.js";
import { programBeneficiaryText } from "@/lib/sectionConfigs";
import { staffTitles } from "@/lib/staffTitles";
import { schoolNameForDocument } from "@/lib/schoolDocument";
import { followupDay, academicFollowups } from "@/lib/caseStudy";

const SCHOOL_LOGOS = {
  abaq: "/branding/school-abaq.png",
  durat: "/branding/school-durat.png",
};
const escapeHTML = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[char]));

const currentSchoolKey = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").school_key || "";
  } catch {
    return "";
  }
};

const currentGender = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").gender || "";
  } catch {
    return "";
  }
};

export const schoolDocumentHeaderHTML = (schoolKey = currentSchoolKey(), schoolName = "", gender = currentGender()) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schoolLogo = SCHOOL_LOGOS[schoolKey] || "";
  const safeSchoolKey = SCHOOL_LOGOS[schoolKey] ? schoolKey : "";
  const safeSchoolName = escapeHTML(schoolNameForDocument(schoolKey, gender, schoolName));
  const cell = "width:33.333%;vertical-align:middle;padding:5px;";
  const gov = "text-align:center;font-size:12px;line-height:1.6;";
  const ministry = `${origin}/branding/ministry-education.png`;
  return `<table data-school-document-header="true" data-school-key="${safeSchoolKey}" dir="rtl" style="width:100%;border-collapse:collapse;margin:0 auto 12px;font-family:Tajawal,sans-serif;"><tbody><tr>
    <td style="${cell}${gov}"><strong>المملكة العربية السعودية</strong><br/>وزارة التعليم<br/>الإدارة العامة للتعليم بمحافظة جدة<br/>مكتب التعليم الخاص بجدة${safeSchoolName ? `<br/><strong>${safeSchoolName}</strong>` : ""}</td>
    <td style="${cell}text-align:center;"><img src="${ministry}" alt="شعار وزارة التعليم" style="height:82px;width:auto;max-width:100%;object-fit:contain;"/></td>
    <td style="${cell}text-align:left;">${schoolLogo ? `<img src="${origin}${schoolLogo}" alt="شعار المدرسة" style="height:112px;width:auto;max-width:100%;object-fit:contain;"/>` : ""}</td>
  </tr></tbody></table>`;
};

export const printSignaturesHTML = (counselorName, principalName, counselorGender, principalGender) => {
  const cell = "direction:rtl;text-align:center;border-top:1px solid #777;padding-top:8px;";
  return `<div data-print-signatures dir="ltr" style="display:grid;grid-template-columns:1fr 1fr;direction:ltr;gap:24px;margin-top:auto;padding-top:18mm;break-inside:avoid;page-break-inside:avoid;">
    <div data-signature="principal" dir="rtl" style="${cell}grid-column:1;grid-row:1;">${staffTitles(principalGender || counselorGender).principal}<br/><strong>${escapeHTML(principalName || "—")}</strong><div>التوقيع: …………………</div></div>
    <div data-signature="counselor" dir="rtl" style="${cell}grid-column:2;grid-row:1;">${staffTitles(counselorGender).counselor}<br/><strong>${escapeHTML(counselorName || "—")}</strong><div>التوقيع: …………………</div></div>
  </div>`;
};

export const exportElementAsPDF = async (element, filename = "report.pdf", options = {}) => {
  if (!element) return;
  const clone = element.cloneNode(true);
  const existingHeader = clone.querySelector("[data-school-document-header]");
  const schoolKey = existingHeader?.getAttribute("data-school-key") || options.schoolKey || currentSchoolKey();
  if (!existingHeader) {
    const header = document.createElement("div");
    header.innerHTML = schoolDocumentHeaderHTML(schoolKey, options.schoolName || "", options.schoolGender || currentGender());
    clone.prepend(header.firstElementChild);
  }
  const imgs = Array.from(clone.querySelectorAll("img"));
  await Promise.all(imgs.map((im) => (im.complete ? null : new Promise((res) => { im.onload = im.onerror = res; }))));
  const opt = {
    margin: options.margin ?? 10,
    filename,
    image: { type: "jpeg", quality: 0.95 },
    html2canvas: { scale: options.scale ?? 2, useCORS: true, logging: false },
    jsPDF: { unit: "mm", format: "a4", orientation: options.orientation || "landscape" },
  };
  return html2pdf().set(opt).from(clone).save();
};

// Helper: build a printable table for a section's records
export const exportRecordsToPDF = (title, columns, records, fields, footer = {}) => {
  const wrap = document.createElement("div");
  wrap.style.direction = "rtl";
  wrap.style.fontFamily = "Tajawal, sans-serif";
  wrap.style.padding = "16px";
  wrap.style.cssText += "box-sizing:border-box;display:flex;flex-direction:column;min-height:260mm;";
  const style = "border:1px solid #ddd;padding:8px;text-align:right;font-size:12px;";
  const escapeHTML = (value) => String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const formatCell = (record, column) => {
    if (column === "beneficiary_scope") return escapeHTML(programBeneficiaryText(record.data));
    const value = record.data[column];
    const followupField = fields.find((field) => field.name === column && field.type === "case_followups");
    if (followupField) {
      const rows = followupField.notesLabel === "الإجراءات" ? academicFollowups(record.data) : value;
      if (!Array.isArray(rows) || !rows.length) return "—";
      const showDay = followupField.showDay !== false;
      return `<table style="width:100%;border-collapse:collapse;"><thead><tr>${[...(showDay ? ["اليوم"] : []), "التاريخ", "نتيجة المتابعة", followupField.notesLabel || "ملاحظات"].map((label) => `<th style="${style}">${label}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${[...(showDay ? [followupDay(row.date)] : []), row.date, row.result, row.notes].map((cell) => `<td style="${style}white-space:pre-wrap;">${escapeHTML(cell || "—")}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    }
    if (column === "students" && record.data.legacy_students?.length) {
      const names = Array.isArray(value) ? value.join("، ") : value || "—";
      return escapeHTML(`${names} — أسماء محفوظة من السجل السابق: ${record.data.legacy_students.join("، ")}`);
    }
    const hasOther = Array.isArray(value)
      ? value.some((item) => /(?:أخرى|اخرى|آخر|اخر)/.test(item || ""))
      : /(?:أخرى|اخرى|آخر|اخر)/.test(value || "");
    const otherText = hasOther && !fields.some((field) => field.name === column && field.customOther)
      ? record.data[`${column}_other`] : "";
    const formattedValue = Array.isArray(value) ? value.join("، ") : value || "—";
    return escapeHTML(otherText ? `${formattedValue}: ${otherText}` : formattedValue);
  };
  const header = `<h1 style="font-family:'IBM Plex Sans Arabic',sans-serif;color:#3E6A56;">${title}</h1>
    <p style="color:#666;font-size:11px;">تاريخ التصدير: ${new Date().toLocaleDateString("ar-EG")}</p>`;
  const th = columns.map(c => `<th style="${style}background:#F9F8F6;font-weight:700;">${fields.find(f => f.name === c)?.label || c}</th>`).join("");
  const rows = records.map((r, i) => {
    const tds = columns.map(c => `<td style="${style}">${formatCell(r, c)}</td>`).join("");
    return `<tr><td style="${style}">${i + 1}</td>${tds}</tr>`;
  }).join("");
  const signatures = printSignaturesHTML(footer.counselor, footer.principal, footer.counselorGender, footer.principalGender);
  wrap.innerHTML = `${header}<table style="width:100%;border-collapse:collapse;"><thead><tr><th style="${style}background:#F9F8F6;">م</th>${th}</tr></thead><tbody>${rows || `<tr><td colspan="${columns.length + 1}" style="${style}text-align:center;">لا توجد سجلات</td></tr>`}</tbody></table>${signatures}`;
  document.body.appendChild(wrap);
  return exportElementAsPDF(wrap, `${title}.pdf`, {
    schoolKey: footer.schoolKey,
    schoolName: footer.schoolName,
    schoolGender: footer.counselorGender,
    orientation: "landscape",
    margin: 7,
    scale: 1.5,
  }).finally(() => wrap.remove());
};

// Elegant behavioral contract print template with signatures
export const exportBehaviorContractPDF = (data, schoolKey = currentSchoolKey(), footer = {}) => {
  const esc = (v) => (Array.isArray(v) ? v.join("، ") : v || "—").toString().replace(/</g, "&lt;");
  const contractItems = Array.isArray(data.items)
    ? data.items
    : ["item1", "item2", "item3", "item4"].map((key) => data[key]).filter(Boolean);
  const items = contractItems
    .map((item) => `<li style="margin-bottom:10px;line-height:1.9;">${esc(item)}</li>`)
    .join("");
  const listOptions = (values) => (Array.isArray(values) ? values : values ? [values] : [])
    .map((value) => `<li style="margin-bottom:6px;line-height:1.8;">${esc(value)}</li>`)
    .join("");
  const noncompliance = listOptions(data.noncompliance);
  const sigBox = (label) => `
    <div style="flex:1;text-align:center;">
      <div style="height:56px;border-bottom:2px dotted #3E6A56;margin-bottom:8px;"></div>
      <div style="font-weight:700;color:#1A201C;font-size:13px;">${label}</div>
    </div>`;
  const wrap = document.createElement("div");
  wrap.style.cssText = "direction:rtl;font-family:Tajawal,sans-serif;padding:20px;color:#1A201C;box-sizing:border-box;";
  wrap.innerHTML = `
    <div style="border:2px solid #3E6A56;border-radius:14px;padding:24px;box-sizing:border-box;display:flex;flex-direction:column;min-height:255mm;">
      <h1 style="text-align:center;color:#3E6A56;font-family:'IBM Plex Sans Arabic',sans-serif;margin:0 0 4px;">التعاقد السلوكي</h1>
      <p style="text-align:center;color:#666;font-size:12px;margin:0 0 20px;">تعاقد سلوكي بين ${staffTitles(footer.counselorGender).counselor} ${esc(data.counselor)} والطالب</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
        <tr>
          <td style="border:1px solid #e6e2d8;padding:9px;background:#F9F8F6;font-weight:700;width:18%;">اسم الطالب</td>
          <td style="border:1px solid #e6e2d8;padding:9px;width:32%;">${esc(data.student)}</td>
          <td style="border:1px solid #e6e2d8;padding:9px;background:#F9F8F6;font-weight:700;width:18%;">الصف</td>
          <td style="border:1px solid #e6e2d8;padding:9px;">${esc(data.grade)}</td>
        </tr>
        <tr>
          <td style="border:1px solid #e6e2d8;padding:9px;background:#F9F8F6;font-weight:700;">يبدأ يوم</td>
          <td style="border:1px solid #e6e2d8;padding:9px;">${esc(data.start)}</td>
          <td style="border:1px solid #e6e2d8;padding:9px;background:#F9F8F6;font-weight:700;">ينتهي يوم</td>
          <td style="border:1px solid #e6e2d8;padding:9px;">${esc(data.end)}</td>
        </tr>
      </table>
      <p style="line-height:2;font-size:14px;">أتعهد أنا الطالب <strong style="color:#3E6A56;">${esc(data.student)}</strong> بالالتزام بالبنود التالية خلال فترة هذا التعاقد:</p>
      <ol style="font-size:14px;padding-right:22px;margin:10px 0 18px;">${items || "<li>—</li>"}</ol>
      <div style="background:#F3EFE6;border:1px solid #e6e2d8;border-radius:10px;padding:14px;margin-bottom:26px;">
        <div style="font-weight:700;color:#3E6A56;margin-bottom:6px;">المكافأة عند الالتزام</div>
        <ul style="line-height:1.9;font-size:14px;margin:0;padding-right:20px;">${listOptions(data.reward) || `<li>${esc(data.reward)}</li>`}</ul>
      </div>
      ${noncompliance ? `<div style="background:#fff;border:1px solid #e6e2d8;border-radius:10px;padding:14px;margin-bottom:26px;"><div style="font-weight:700;color:#3E6A56;margin-bottom:6px;">في حالة عدم الالتزام</div><ul style="line-height:1.9;font-size:14px;margin:0;padding-right:20px;">${noncompliance}</ul></div>` : ""}
      <div style="display:flex;gap:28px;margin-top:36px;">
        ${sigBox("توقيع الطالب")}
        ${sigBox("توقيع ولي الأمر")}
      </div>
      ${printSignaturesHTML(footer.counselor || data.counselor, footer.principal, footer.counselorGender, footer.principalGender)}
    </div>
    <p style="text-align:center;color:#999;font-size:10px;margin-top:14px;">حُرر بتاريخ ${new Date().toLocaleDateString("ar-EG")} — شركة كيان النموذجية</p>`;
  document.body.appendChild(wrap);
  return exportElementAsPDF(wrap, `تعاقد-سلوكي-${data.student || ""}.pdf`, {
    schoolKey, schoolGender: footer.counselorGender,
  }).finally(() => wrap.remove());
};

// Combined monthly reports of all counselors for a given month (supervisor)
export const exportCombinedMonthlyPDF = (month, reports) => {
  const esc = (v) => (v || "—").toString().replace(/</g, "&lt;");
  const cell = "border:1px solid #e6e2d8;padding:7px;font-size:12px;text-align:right;";
  const sections = reports.map((r) => {
    const programs = Object.entries(r.data.programs || {})
      .filter(([, v]) => v?.done)
      .map(([k, v]) => `<tr><td style="${cell}">${esc(k)}</td><td style="${cell}">${esc(v.notes)}</td></tr>`)
      .join("");
    const cases = Object.entries(r.data.cases || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `<span style="display:inline-block;background:#F3EFE6;border:1px solid #e6e2d8;border-radius:8px;padding:3px 8px;margin:2px;font-size:11px;">${esc(k)}: <b>${v}</b></span>`)
      .join("");
    return `<div style="border:1px solid #e6e2d8;border-radius:12px;padding:16px;margin-bottom:16px;page-break-inside:avoid;">
      ${schoolDocumentHeaderHTML(r.school_key, r.school_name, r.gender)}
      <h2 style="color:#3E6A56;font-family:'IBM Plex Sans Arabic',sans-serif;margin:0 0 4px;font-size:17px;">${esc(r.counselor_name)}</h2>
      <p style="color:#666;font-size:11px;margin:0 0 10px;">المدرسة: ${esc(r.data.school)} — القسم: ${esc(r.data.department)}</p>
      <div style="font-weight:700;font-size:13px;margin-bottom:5px;">البرامج المنفذة</div>
      ${programs
        ? `<table style="width:100%;border-collapse:collapse;margin-bottom:10px;"><thead><tr><th style="${cell}background:#F9F8F6;">البرنامج</th><th style="${cell}background:#F9F8F6;">ملاحظات</th></tr></thead><tbody>${programs}</tbody></table>`
        : `<p style="color:#999;font-size:12px;">لا توجد برامج منفذة</p>`}
      <div style="font-weight:700;font-size:13px;margin-bottom:5px;">الحالات المتابعة</div>
      <div style="margin-bottom:10px;">${cases || `<span style="color:#999;font-size:12px;">لا توجد حالات</span>`}</div>
      ${r.data.suggestions ? `<div style="font-weight:700;font-size:13px;margin-bottom:4px;">ملاحظات واقتراحات</div><p style="font-size:12px;line-height:1.8;margin:0;">${esc(r.data.suggestions)}</p>` : ""}
      ${printSignaturesHTML(r.data.counselor_name || r.counselor_name, r.data.principal_name || r.principal_name, r.gender, r.principal_gender)}
    </div>`;
  }).join("");
  const wrap = document.createElement("div");
  wrap.style.cssText = "direction:rtl;font-family:Tajawal,sans-serif;padding:16px;color:#1A201C;";
  wrap.innerHTML = `<h1 style="text-align:center;color:#3E6A56;font-family:'IBM Plex Sans Arabic',sans-serif;">التقارير الشهرية المجمعة — شهر ${esc(month)}</h1>
    <p style="text-align:center;color:#666;font-size:11px;margin-bottom:18px;">عدد التقارير: ${reports.length} — تاريخ التصدير: ${new Date().toLocaleDateString("ar-EG")}</p>
    ${sections}`;
  document.body.appendChild(wrap);
  return exportElementAsPDF(wrap, `التقارير-الشهرية-المجمعة-${month}.pdf`).finally(() => wrap.remove());
};
